import './DeliveryViewToggle.css'

export type DeliveryView = 'today' | 'week' | 'month' | 'previous'

/**
 * Four views for four jobs: work today (the field default), plan the week
 * (office), look a month out at what is inbound, find the stray (a truck
 * whose date slipped, now at the gate).
 *
 * Both Week and the 30-day view are rolling windows on today, not calendar
 * weeks or months — "what is coming in the next 30 days" is the question the
 * yard asks, and a calendar month would shrink to a single day by the 30th.
 * The label says "30 days" rather than "Month" so it cannot be read as the
 * calendar kind.
 *
 * Previous day(s) rides along in every view — outstanding trucks are never
 * silently hidden — so Previous day(s) is a focus mode, not the only place they
 * exist. The counts come from the whole board, unfiltered: a narrow view can
 * never make the maroon number go away, and neither can folding the section
 * (see DeliveryTileGrid).
 *
 * Named "Previous day(s)", not "Past due", and deliberately: past-due-ness is
 * not what the bucket selects on. A truck due 08:00 today and still missing at
 * 14:00 *is* past due, and it sits in Today — so "Past due" would promise a
 * completeness this view does not have. "Previous day(s)" names the actual
 * contents, and being overdue follows from that rather than defining it. It
 * also leaves `Late` as the only per-row word and this as the only date-bucket
 * word, with no overlap between them.
 */
type Props = {
  view: DeliveryView
  onChange: (view: DeliveryView) => void
  counts: { today: number; week: number; month: number; previous: number }
}

function DeliveryViewToggle({ view, onChange, counts }: Props) {
  const options: { key: DeliveryView; label: string; count: number }[] = [
    { key: 'today', label: 'Today', count: counts.today },
    { key: 'week', label: 'Week', count: counts.week },
    { key: 'month', label: '30 days', count: counts.month },
    { key: 'previous', label: 'Previous day(s)', count: counts.previous },
  ]

  return (
    <div className="delivery-views" role="group" aria-label="Delivery view">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={
            option.key === 'previous'
              ? 'delivery-views__option delivery-views__option--alert'
              : 'delivery-views__option'
          }
          aria-pressed={view === option.key}
          onClick={() => onChange(option.key)}
        >
          {option.label}{' '}
          <span className="delivery-views__count">{option.count}</span>
        </button>
      ))}
    </div>
  )
}

export default DeliveryViewToggle
